/** Empty state when search returns no results. */

import { h } from 'preact';

interface PopularCategory {
  name: string;
  query: string;
}

interface NoResultsProps {
  query: string;
  suggestion: string | null | undefined;
  didYouMean: string | null | undefined;
  relaxedQueries: Array<{ query: string; result_count: number }>;
  /** Static Terra-curated category chips shown when there are no other recovery
   *  affordances (no spell suggestion, no relaxed-query results). Lets the
   *  customer pivot from a dead-end search into a known-good catalog branch. */
  popularCategories?: PopularCategory[];
  onSearch: (query: string) => void;
}

const DEFAULT_POPULAR_CATEGORIES: PopularCategory[] = [
  { name: 'Desiccators', query: 'desiccator' },
  { name: 'Cleanrooms', query: 'cleanroom' },
  { name: 'Pass-Throughs', query: 'pass-through' },
  { name: 'Glove Boxes', query: 'glove box' },
  { name: 'Fume Hoods', query: 'fume hood' },
  { name: 'Laminar Flow Hoods', query: 'laminar flow hood' },
];

export function NoResults({
  query,
  suggestion,
  didYouMean,
  relaxedQueries,
  popularCategories,
  onSearch,
}: NoResultsProps) {
  const spellSuggestion = didYouMean || suggestion;
  const hasRelaxed = relaxedQueries && relaxedQueries.length > 0;
  const categories = popularCategories ?? DEFAULT_POPULAR_CATEGORIES;
  const displayQuery = query.trim();

  return (
    <div class="ts-no-results">
      <svg class="ts-no-results-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
        <path d="M8 11h6" />
      </svg>
      <h3 class="ts-no-results-title">
        {displayQuery ? `No results for "${displayQuery}"` : 'No results'}
      </h3>

      {spellSuggestion && (
        <p class="ts-no-results-suggestion">
          Did you mean{' '}
          <button class="ts-link" onClick={() => onSearch(spellSuggestion)}>
            {spellSuggestion}
          </button>
          ?
        </p>
      )}

      {hasRelaxed && (
        <div class="ts-no-results-relaxed">
          <p class="ts-no-results-text">Try one of these related searches:</p>
          <div class="ts-no-results-relaxed-list">
            {relaxedQueries.map((rq) => (
              <button
                key={rq.query}
                class="ts-no-results-relaxed-item"
                onClick={() => onSearch(rq.query)}
              >
                {rq.query}
                <span class="ts-no-results-relaxed-count">{rq.result_count} results</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!spellSuggestion && !hasRelaxed && (
        <p class="ts-no-results-text">
          Try checking your spelling, using fewer keywords, or searching for a related term.
        </p>
      )}

      {/* Popular categories — surfaced when the customer has no other recovery
          path. Always shown alongside spell suggestion if present so a wrong
          spelling doesn't dead-end on a single chip. Hidden once relaxedQueries
          is providing real query alternatives. */}
      {!hasRelaxed && categories.length > 0 && (
        <div class="ts-no-results-categories">
          <p class="ts-no-results-categories-label">Browse popular categories</p>
          <div class="ts-no-results-categories-list">
            {categories.slice(0, 6).map((cat) => (
              <button
                key={cat.query}
                class="ts-no-results-category"
                onClick={() => onSearch(cat.query)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div class="ts-no-results-tips">
        <p><strong>Search tips:</strong></p>
        <ul>
          <li>Search by part number (e.g., "6602-69-PC")</li>
          <li>Use general terms (e.g., "cleanroom door" instead of specific dimensions)</li>
          <li>Try product categories (e.g., "FFU", "glove box", "pass-through")</li>
        </ul>
      </div>
    </div>
  );
}
